import React, { useEffect } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export interface FAQItem {
  question: string;
  answer: string;
  id?: string;
}

interface FAQSectionProps {
  title?: string;
  faqs: FAQItem[];
  className?: string;
  id?: string; // For multiple FAQSections on a page, if ever needed
  injectJsonLd?: boolean;
}

const FAQSection: React.FC<FAQSectionProps> = ({ 
  title = "Frequently Asked Questions",
  faqs,
  className = "",
  id = 'faq-main',
  injectJsonLd = true,
}) => {
  useEffect(() => {
    const scriptId = `faq-schema-${id}`;
    const existingScript = document.getElementById(scriptId);

    if (!injectJsonLd || !faqs || faqs.length === 0) {
      if (existingScript) {
        existingScript.remove();
      }
      return;
    }

    let script = existingScript as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': faqs.map(faq => ({
        '@type': 'Question',
        'name': faq.question,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': faq.answer,
        },
      })),
    };
    script.innerHTML = JSON.stringify(faqSchema, null, 2);

    return () => {
      const currentScript = document.getElementById(scriptId);
      if (currentScript) {
        currentScript.remove();
      }
    };
  }, [faqs, id, injectJsonLd]);

  if (!faqs || faqs.length === 0) {
    return null;
  }

  return (
    <section className={`w-full ${className}`} aria-labelledby={`faq-title-${id}`}>
      <div className="container mx-auto px-4">
        <h2 
          id={`faq-title-${id}`}
          className="text-3xl font-bold text-center mb-8"
        >
          {title}
        </h2>
        
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={faq.id || `faq-${index}`} 
                value={faq.id || `faq-${index}`}
                className="border-b border-border"
                itemScope
                itemType="https://schema.org/Question"
              >
                <AccordionTrigger 
                  className="text-left hover:no-underline py-4"
                  itemProp="name"
                >
                  <span className="font-medium">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent 
                  className="pb-4 text-muted-foreground leading-relaxed"
                  itemProp="acceptedAnswer"
                  itemScope
                  itemType="https://schema.org/Answer"
                >
                  <div itemProp="text">
                    {faq.answer}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
